import React from 'react';
import clsx from 'clsx';
import type {IconProp} from '@fortawesome/fontawesome-svg-core';
import {
	FontAwesomeIcon,
	type FontAwesomeIconProps,
} from '@fortawesome/react-fontawesome';
import {capitalize, touchRippleClasses} from '@mui/material';
import MuiIconButton, {
	iconButtonClasses as muiIconButtonClasses,
	type IconButtonProps as MuiIconButtonProps,
} from '@mui/material/IconButton';
import {alpha} from '@mui/system';
import type {OverridableStringUnion} from '@mui/types';
import {
	composeClasses,
	getSeverityColors,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import Tooltip, {type TooltipProps} from '@netx/core-components/Tooltip';
import {
	getIconButtonUtilityClass,
	ICON_BUTTON_CLASS_NAME,
	iconButtonClasses,
	type IconButtonClasses,
} from './iconButtonClasses';

export const ICON_BUTTON_SIZE = {
	small: 28,
	medium: 42,
	large: 56,
} as const;

export const ICON_SIZE = {
	small: 12,
	medium: 18,
	large: 24,
} as const;

export type IconButtonColor = Required<MuiIconButtonProps>['color'];

type IconButtonOwnerState = WithClasses<
	IconButtonClasses,
	Required<Pick<IconButtonProps, 'color' | 'variant' | 'size'>> & {
		contrasted?: boolean;
		squaredBorder?: boolean;
		iconSize: 'small' | 'medium' | 'large';
	}
>;

const useUtilityClasses = (ownerState: IconButtonOwnerState) => {
	const {classes, color, variant} = ownerState;
	const slots = {
		root: [
			'root',
			(variant && variant) || 'default',
			variant && `${variant}${capitalize(color)}`,
		],
		icon: ['icon'],
	};
	return composeClasses(slots, getIconButtonUtilityClass, classes);
};

const IconButtonRoot = styled(MuiIconButton, {
	name: ICON_BUTTON_CLASS_NAME,
	slot: 'Root',
	overridesResolver: (props, styles) => styles.root,
})(({theme, ...props}) => {
	const ownerState = props.ownerState as IconButtonOwnerState;
	const colors = getSeverityColors(theme);
	const borderRadius = ownerState.squaredBorder ? '4px' : '50%';

	return {
		borderRadius,
		height: ICON_BUTTON_SIZE[ownerState.size],
		width: ICON_BUTTON_SIZE[ownerState.size],

		[`& .${touchRippleClasses.child}`]: {
			borderRadius,
		},

		...(ownerState.variant === 'fab'
			? {
					boxShadow: theme.shadows[4],
					transition: theme.transitions.create(
						['background-color', 'box-shadow'],
						{
							duration: theme.transitions.duration.short,
						},
					),
					'&:active': {
						boxShadow: theme.shadows[6],
					},
					'&:hover': {
						background: theme.palette.secondary.light,
					},
					[`&.${muiIconButtonClasses.disabled}`]: {
						boxShadow: 'none',
					},
				}
			: {}),

		...(ownerState.variant === 'fab' || ownerState.variant === 'contained'
			? {
					fontWeight: theme.typography.fontWeightMedium,
					...(ownerState.color === 'default'
						? {
								backgroundColor: theme.palette.secondary.main,
								color: `${theme.palette.secondary.contrastText} !important`,
								'&:hover': {
									background: colors[ownerState.color].hover,
								},
							}
						: ownerState.color === 'inherit'
							? {
									color: 'inherit',
								}
							: {
									backgroundColor: colors[ownerState.color]?.default,
									color: `${colors[ownerState.color]?.text} !important`,
									'&:hover': {
										background: colors[ownerState.color].hover,
									},
								}),
				}
			: ownerState.variant === 'outlined'
				? {
						borderWidth: 1,
						borderStyle: 'solid',
						...(ownerState.color === 'default'
							? {
									borderColor: theme.palette.custom.border,
								}
							: ownerState.color === 'inherit'
								? {
										borderColor: 'inherit',
									}
								: {
										borderColor: colors[ownerState.color]?.default,
									}),
					}
				: {}),

		...(ownerState.variant === 'default' || ownerState.variant === 'outlined'
			? {
					color:
						ownerState.color === 'inherit'
							? 'inherit'
							: ownerState.color === 'default'
								? undefined
								: colors[ownerState.color]?.default,
				}
			: {}),

		...(ownerState.variant !== 'fab' && ownerState.contrasted
			? {
					background: alpha(theme.palette.background.default, 0.85),
					height: 32,
					width: 32,
					// transition: theme.transitions.create('opacity', {
					// 	duration: theme.transitions.duration.short,
					// 	easing: theme.transitions.easing.easeInOut,
					// }),
					// [`&.${iconButtonToggleClasses.on}`]: {
					// 	boxShadow: theme.shadows[4],
					// },
					'&:hover': {
						background: theme.palette.background.default,
						// [`&:not(.${iconButtonToggleClasses.on})`]: {
						// 	color: theme.palette.primary.main,
						// },
					},
					// [`& .${iconButtonClasses.icon}`]: {
					// 	height: 12,
					// 	width: 12,
					// },
				}
			: {}),

		[`&.${muiIconButtonClasses.disabled}`]: {
			opacity: 0.5,
			[`&.${iconButtonClasses.outlined}, &.${iconButtonClasses.default}`]: {
				color:
					ownerState.color === 'inherit'
						? 'inherit'
						: ownerState.color === 'default'
							? undefined
							: colors[ownerState.color]?.default,
			},
			[`&.${iconButtonClasses.fab}, &.${iconButtonClasses.contained}`]: {
				backgroundColor:
					ownerState.color === 'inherit'
						? 'inherit'
						: ownerState.color === 'default'
							? undefined
							: colors[ownerState.color]?.default,
				color: theme.palette.common.white,
			},
		},

		[`& .${iconButtonClasses.icon}`]: {
			height: ICON_SIZE[ownerState.iconSize],
			width: ICON_SIZE[ownerState.iconSize],
		},
	};
});

const IconButtonIconRoot = styled(FontAwesomeIcon, {
	name: ICON_BUTTON_CLASS_NAME,
	slot: 'Icon',
	overridesResolver: (props, styles) => styles.icon,
})(({theme}) => ({
	zIndex: 2,
	// Compensate for the lopsidedness of the play icon
	'&.fa-play': {
		marginLeft: 3,
	},
}));

export interface IconButtonPropsSizeOverrides {}

interface IconProps extends Partial<Omit<FontAwesomeIconProps, 'icon'>> {}

export interface IconButtonProps extends WithClasses<
	IconButtonClasses,
	Omit<Partial<MuiIconButtonProps>, 'children'>
> {
	children?: React.ReactNode;
	'data-testid'?: string;
	avatar?: React.ReactElement;
	icon?: IconProp;
	iconSize?: 'small' | 'medium' | 'large';
	label: string;
	/**
	 * Variant of the button.
	 */
	variant?: OverridableStringUnion<
		'fab' | 'outlined' | 'contained' | 'default',
		IconButtonPropsSizeOverrides
	>;
	/**
	 * Props to pass along to `FontAwesomeIcon` component.
	 * `size` is defined by the `IconButton` props.
	 */
	IconProps?: Omit<IconProps, 'size'>;
	/**
	 * Props to pass along to `Tooltip` component.
	 */
	TooltipProps?: Partial<TooltipProps>;
	squaredBorder?: boolean;
	contrasted?: boolean;
}

export const IconButtonPrimitive = (
	{
		classes: _classes,
		children,
		className,
		avatar,
		color,
		contrasted,
		disabled,
		icon,
		iconSize,
		label,
		size = 'medium',
		title,
		variant = 'default',
		IconProps,
		TooltipProps,
		squaredBorder,
		...props
	}: IconButtonProps,
	ref: React.ForwardedRef<HTMLButtonElement>,
) => {
	color = color ?? (variant === 'fab' ? 'secondary' : 'default');

	const ownerState: IconButtonOwnerState = {
		classes: _classes,
		color,
		contrasted,
		iconSize: iconSize ?? size,
		size,
		variant,
		squaredBorder,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<Tooltip
			disabled={disabled}
			title={label}
			data-testid={'icon-button-label'}
			{...TooltipProps}
		>
			<IconButtonRoot
				data-testid="icon-button"
				type="button"
				{...props}
				className={clsx(classes.root, className)}
				color={color}
				disabled={disabled}
				ownerState={ownerState}
				ref={ref}
				size={size}
			>
				{icon != null && (
					<IconButtonIconRoot
						fixedWidth
						className={classes.icon}
						data-testid="icon-button-icon"
						{...IconProps}
						icon={icon}
					/>
				)}
				{avatar}
				{children}
			</IconButtonRoot>
		</Tooltip>
	);
};

export const IconButton = React.memo(React.forwardRef(IconButtonPrimitive)) as (
	props: IconButtonProps,
) => JSX.Element;

export default IconButton;
